/**
 * DrawToolbar.js
 *
 * Toolbar component for chart drawing mode.
 *
 * WHY THIS EXISTS:
 * - Users need easy access to drawing tools when analyzing charts
 * - Collapses to single button when not in use to save space
 * - Provides quick access to Ask AI and Clear All actions
 *
 * STATES:
 * - Collapsed: Shows only "Draw" button to enter draw mode
 * - Expanded: Shows all tools + actions when in draw mode
 */
import React from 'react';
import {
  FaMapPin,
  FaCircle,
  FaArrowRight,
  FaSquare,
  FaMinus,
  FaTimes,
  FaTrash,
  FaCommentDots,
  FaPencilAlt,
  FaFont
} from 'react-icons/fa';
import './DrawToolbar.css';

/**
 * Available drawing tools with their icons and labels.
 * Order determines toolbar display order.
 */
const TOOLS = [
  { id: 'pin', icon: FaMapPin, label: 'Pin - Click to place a marker' },
  { id: 'circle', icon: FaCircle, label: 'Circle - Drag to draw an ellipse' },
  { id: 'arrow', icon: FaArrowRight, label: 'Arrow - Drag to draw an arrow' },
  { id: 'rectangle', icon: FaSquare, label: 'Rectangle - Drag to draw a box' },
  { id: 'line', icon: FaMinus, label: 'Line - Drag to draw a line' },
  { id: 'text', icon: FaFont, label: 'Text - Click to add text annotation' },
];

/**
 * DrawToolbar - Provides drawing tools and actions for chart annotation.
 *
 * @param {Object} props
 * @param {boolean} props.isDrawMode - Whether draw mode is active
 * @param {string|null} props.activeTool - Currently selected tool ID
 * @param {number} props.drawingCount - Number of drawings on chart
 * @param {Function} props.onEnterDrawMode - Callback to enter draw mode
 * @param {Function} props.onExitDrawMode - Callback to exit draw mode
 * @param {Function} props.onSelectTool - Callback when tool is selected
 * @param {Function} props.onClearAll - Callback to clear all drawings
 * @param {Function} props.onAskAI - Callback to ask AI about drawings
 */
export function DrawToolbar({
  isDrawMode,
  activeTool,
  drawingCount,
  onEnterDrawMode,
  onExitDrawMode,
  onSelectTool,
  onClearAll,
  onAskAI,
}) {
  // Collapsed state - just show "Draw" button
  if (!isDrawMode) {
    return (
      <div className="draw-toolbar-collapsed">
        <button
          className="draw-toggle-btn"
          onClick={() => onEnterDrawMode('pin')}
          title="Draw on chart to mark areas for AI analysis"
          aria-label="Enter draw mode"
        >
          <FaPencilAlt /> Draw
        </button>
      </div>
    );
  }

  // Expanded state - full toolbar
  return (
    <div className="draw-toolbar" role="toolbar" aria-label="Drawing tools">
      {/* Drawing tools */}
      <div className="draw-toolbar-tools" role="group" aria-label="Drawing tools">
        {TOOLS.map(tool => (
          <button
            key={tool.id}
            className={`draw-tool-btn ${activeTool === tool.id ? 'active' : ''}`}
            onClick={() => onSelectTool(tool.id)}
            title={tool.label}
            aria-label={tool.label}
            aria-pressed={activeTool === tool.id}
          >
            <tool.icon />
          </button>
        ))}
      </div>

      <div className="draw-toolbar-divider" role="separator" />

      {/* Actions */}
      <div className="draw-toolbar-actions">
        {drawingCount > 0 && (
          <>
            {/* Clear button - placed BEFORE Ask AI, styled red to indicate destructive action */}
            <button
              className="draw-action-btn draw-clear"
              onClick={onClearAll}
              title="Clear all drawings"
              aria-label="Clear all drawings"
            >
              <FaTrash />
            </button>
            <button
              className="draw-action-btn draw-ask-ai"
              onClick={onAskAI}
              title="Ask AI to analyze the marked areas"
              aria-label={`Ask AI about ${drawingCount} drawing${drawingCount !== 1 ? 's' : ''}`}
            >
              <FaCommentDots /> Ask AI ({drawingCount})
            </button>
          </>
        )}
        <button
          className="draw-action-btn draw-close"
          onClick={onExitDrawMode}
          title="Exit draw mode"
          aria-label="Exit draw mode"
        >
          <FaTimes />
        </button>
      </div>
    </div>
  );
}

export default DrawToolbar;
